import {
  Bar,
  BarChart as RechartsBar,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartFrame, ChartTooltip, type TableColumn } from "./chart-frame";
import { AXIS_PROPS, CHROME, GRID_PROPS, MARK, MAX_SERIES, seriesColor, STATUS } from "./tokens";

export interface StackSeries {
  key: string;
  label: string;
  /** Overrides the categorical slot. Use only where the value MEANS good/bad. */
  color?: string;
}

interface StackedBarProps {
  title: string;
  note?: string;
  /** Each row is one category; series keys hold the segment values. */
  data: Record<string, string | number>[];
  categoryKey: string;
  series: StackSeries[];
  loading?: boolean;
  error?: string | null;
  orientation?: "vertical" | "horizontal";
  height?: number;
  className?: string;
  emptyBody?: string;
  /** Renders each stack as a share of 100% instead of raw counts. */
  percentage?: boolean;
}

/** Status tokens for the attendance/leave stacks, where the value means state. */
export const STATUS_SERIES_COLOR: Record<string, string> = {
  complete: STATUS.good,
  approved: STATUS.good,
  half: STATUS.warn,
  pending: STATUS.warn,
  absent: STATUS.bad,
  rejected: STATUS.bad,
  rest: STATUS.neutral,
  holiday: STATUS.neutral,
  // Leave handed back. Neutral, like rest and holiday: it is not a refusal, and
  // wearing the rejection red would read as one.
  withdrawn: STATUS.neutral,
  // Leave is a state, but it is not good/warn/bad — a sanctioned day away is
  // none of those, and reusing one of them would say something the data doesn't.
  // The accent reads as its own fourth state and is the one hue in this set that
  // is far from all three: validated against the app's surfaces, its worst
  // adjacent pair here is ΔE 22 (protan) / 26 (normal), against a target of 8.
  // Always shipped with a label and, on badges, an icon.
  leave: STATUS.leave,
};

/**
 * Part-to-whole across categories. Segments are separated by a 2px gap in the
 * surface colour rather than a stroke, so no non-data ink is added.
 *
 * Interior segments never carry an inline label (there is no free end to place
 * one against without risking a clip) — the legend and tooltip carry identity,
 * and the table view carries every value.
 */
export function StackedBarChart({
  title,
  note,
  data,
  categoryKey,
  series,
  loading,
  error,
  orientation = "vertical",
  height = 240,
  className,
  emptyBody,
  percentage,
}: StackedBarProps) {
  const usable = series.slice(0, MAX_SERIES);
  const colorFor = (s: StackSeries, index: number) => s.color ?? seriesColor(index);

  const columns: TableColumn[] = [
    { key: categoryKey, header: "Category" },
    ...usable.map((s) => ({ key: s.key, header: s.label, numeric: true })),
    { key: "__total", header: "Total", numeric: true },
  ];

  const rows = data.map((row) => {
    const total = usable.reduce((sum, s) => sum + Number(row[s.key] ?? 0), 0);
    const out: Record<string, string> = { [categoryKey]: String(row[categoryKey] ?? "") };
    for (const s of usable) out[s.key] = String(row[s.key] ?? 0);
    out.__total = String(total);
    return out;
  });

  const legend = usable.map((s, i) => ({ label: s.label, color: colorFor(s, i) }));
  const isHorizontal = orientation === "horizontal";
  const labelWidth = Math.min(
    150,
    Math.max(72, ...data.map((d) => String(d[categoryKey] ?? "").length * 6.6 + 12)),
  );

  return (
    <ChartFrame
      title={title}
      {...(note ? { note } : {})}
      legend={legend}
      table={{ columns, rows }}
      {...(loading ? { loading } : {})}
      {...(error ? { error } : {})}
      empty={!loading && !error && data.length === 0}
      {...(emptyBody ? { emptyBody } : {})}
      height={height}
      {...(className ? { className } : {})}
    >
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBar
          data={data}
          layout={isHorizontal ? "vertical" : "horizontal"}
          margin={
            isHorizontal
              ? { top: 4, right: 12, bottom: 4, left: 0 }
              : { top: 8, right: 8, bottom: 4, left: -16 }
          }
          barCategoryGap={MARK.gap * 3}
          stackOffset={percentage ? "expand" : "none"}
        >
          <CartesianGrid {...GRID_PROPS} horizontal={!isHorizontal} vertical={isHorizontal} />
          {isHorizontal ? (
            <>
              <XAxis
                type="number"
                {...AXIS_PROPS}
                allowDecimals={false}
                {...(percentage
                  ? { tickFormatter: (v: number) => `${Math.round(v * 100)}%`, domain: [0, 1] }
                  : {})}
              />
              <YAxis type="category" dataKey={categoryKey} width={labelWidth} {...AXIS_PROPS} axisLine={false} />
            </>
          ) : (
            <>
              <XAxis dataKey={categoryKey} {...AXIS_PROPS} interval={0} />
              <YAxis
                {...AXIS_PROPS}
                allowDecimals={false}
                axisLine={false}
                {...(percentage
                  ? { tickFormatter: (v: number) => `${Math.round(v * 100)}%`, domain: [0, 1] }
                  : {})}
              />
            </>
          )}
          <Tooltip cursor={{ fill: CHROME.grid, fillOpacity: 0.5 }} content={<ChartTooltip />} />
          {usable.map((s, index) => (
            <Bar
              key={s.key}
              dataKey={s.key}
              name={s.label}
              stackId="stack"
              fill={colorFor(s, index)}
              maxBarSize={MARK.maxBarSize}
              /* The surface-coloured stroke IS the 2px gap between segments —
                 drawn in the surface colour, so it separates without adding ink. */
              stroke={CHROME.surface}
              strokeWidth={MARK.gap}
              isAnimationActive={false}
              radius={
                index === usable.length - 1
                  ? isHorizontal
                    ? MARK.radiusRight
                    : MARK.radiusUp
                  : undefined
              }
            />
          ))}
        </RechartsBar>
      </ResponsiveContainer>
    </ChartFrame>
  );
}
